import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  LinearProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { CloudUpload, ArrowBack, DeleteForever } from '@mui/icons-material';
import { issuesAPI, authAPI } from '../services/api';
import { colors } from '../theme';

const AdminUploadPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [selectedIndesignFiles, setSelectedIndesignFiles] = useState({
    district: null,
    clubs: null,
    international: null,
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
   // Confirmation Dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [existingIssueId, setExistingIssueId] = useState(null);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // Check authentication
  React.useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  const uploadMutation = useMutation(
    (formData) => issuesAPI.create(formData),
    {
      onSuccess: () => {
        setSuccess('上傳成功！檔案已上傳並在背景處理中，請稍後回來查看結果。');
        setUploadProgress(100);
        setSelectedPdfFile(null);
        setSelectedIndesignFiles({ district: null, clubs: null, international: null });
        reset();
        queryClient.invalidateQueries('issues');
        
        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      },
      onError: (err) => {
        console.error('Upload error:', err);
        let errorMessage = '上傳錯誤';
        
        if (err.response?.data) {
          const data = err.response.data;
          
          if (data.message) {
            errorMessage += ': ' + data.message;
          } else if (data.non_field_errors) {
            const nonFieldErrors = Array.isArray(data.non_field_errors) 
              ? data.non_field_errors 
              : [data.non_field_errors];
            
            // Check for duplicate issue error
            const isDuplicate = nonFieldErrors.some(e => 
              typeof e === 'string' && e.includes('already exists')
            );

            if (isDuplicate) {
              errorMessage = '錯誤：該年份和季度的期刊已存在。請勿重複上傳。';
            } else {
              errorMessage += ': ' + nonFieldErrors.join(', ');
            }
          } else {
            // Handle field-specific errors
            const fieldErrors = Object.entries(data)
              .map(([key, val]) => {
                const errorText = Array.isArray(val) ? val.join(', ') : val;
                return `${key}: ${errorText}`;
              })
              .join('; ');
              
            if (fieldErrors) {
              errorMessage += ': ' + fieldErrors;
            } else {
              errorMessage += ': ' + JSON.stringify(data);
            }
          }
        } else {
          errorMessage += ': ' + err.message;
        }
        
        setError(errorMessage);
        setUploadProgress(0);
      },
    }
  );

  const executeUpload = async (data) => {
    try {
      if (!selectedPdfFile) {
        setError('請選擇PDF檔案');
        return;
      }

      setError('');
      setSuccess('');
      setUploadProgress(5);

      // 1. Upload PDF
      setSuccess('正在上傳 PDF...');
      const pdfContentType = selectedPdfFile.type || 'application/pdf';
      const pdfRes = await issuesAPI.getUploadUrl(selectedPdfFile.name, pdfContentType, 'pdf');
      await issuesAPI.uploadToGcs(pdfRes.data.upload_url, selectedPdfFile, pdfContentType);
      const pdfPath = pdfRes.data.file_path;
      setUploadProgress(40);

      // 2. Upload InDesign files if present
      let districtPath = null;
      if (selectedIndesignFiles.district) {
        setSuccess('正在上傳分區 InDesign...');
        const res = await issuesAPI.getUploadUrl(selectedIndesignFiles.district.name, 'application/zip', 'district');
        await issuesAPI.uploadToGcs(res.data.upload_url, selectedIndesignFiles.district, 'application/zip');
        districtPath = res.data.file_path;
      }
      setUploadProgress(60);

      let clubsPath = null;
      if (selectedIndesignFiles.clubs) {
        setSuccess('正在上傳屬會 InDesign...');
        const res = await issuesAPI.getUploadUrl(selectedIndesignFiles.clubs.name, 'application/zip', 'clubs');
        await issuesAPI.uploadToGcs(res.data.upload_url, selectedIndesignFiles.clubs, 'application/zip');
        clubsPath = res.data.file_path;
      }
      setUploadProgress(80);

      let internationalPath = null;
      if (selectedIndesignFiles.international) {
        setSuccess('正在上傳國際 InDesign...');
        const res = await issuesAPI.getUploadUrl(selectedIndesignFiles.international.name, 'application/zip', 'international');
        await issuesAPI.uploadToGcs(res.data.upload_url, selectedIndesignFiles.international, 'application/zip');
        internationalPath = res.data.file_path;
      }
      setUploadProgress(90);

      // 3. Create Issue with paths
      setSuccess('正在建立期刊記錄...');
      const formData = {
        publication_year: data.publication_year,
        publication_quarter: data.publication_quarter,
        pdf_file_path: pdfPath,
        skip_pages: data.skip_pages || '',
      };

      if (districtPath) formData.district_indesign_path = districtPath;
      if (clubsPath) formData.clubs_indesign_path = clubsPath;
      if (internationalPath) formData.international_indesign_path = internationalPath;

      uploadMutation.mutate(formData);
      
    } catch (err) {
      console.error(err);
      setError('上傳失敗: ' + (err.response?.data?.message || err.message));
      setUploadProgress(0);
    }
  };

  const onSubmit = async (data) => {
    // Check for Duplicate Issue first
    try {
      const response = await issuesAPI.checkExists(data.publication_year, data.publication_quarter);
      // Depending on API pagination, check results array
      const existing = response.data.results ? response.data.results[0] : null;

      if (existing) {
        setExistingIssueId(existing.id);
        setPendingFormData(data);
        setConfirmDialogOpen(true);
        return;
      }

      await executeUpload(data);
    } catch (err) {
      console.error('Check exists failed:', err);
      // Fallback to normal upload if check fails
      await executeUpload(data);
    }
  };

  const handleConfirmOverwrite = async () => {
    if (!existingIssueId) return;
    
    setIsDeleting(true);
    try {
      await issuesAPI.delete(existingIssueId);
      setConfirmDialogOpen(false);
      setIsDeleting(false);
      setSuccess('舊期刊已刪除，開始上傳新期刊...');
      
      // Proceed with upload
      if (pendingFormData) {
        await executeUpload(pendingFormData);
      }
    } catch (err) {
      console.error('Delete failed:', err);
      setError('刪除舊期刊失敗: ' + (err.response?.data?.message || err.message));
      setIsDeleting(false);
      setConfirmDialogOpen(false);
    }
  };

  const handleCancelOverwrite = () => {
    setConfirmDialogOpen(false);
    setExistingIssueId(null);
    setPendingFormData(null);
  };


  const handlePdfFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('只接受 PDF 檔案');
        return;
      }
      if (file.size > 500 * 1024 * 1024) {
        setError('檔案大小必須小於 500MB');
        return;
      }
      setSelectedPdfFile(file);
      setError('');
    }
  };

  const handleIndesignFileChange = (section) => (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        setError('InDesign 套件必須是 ZIP 檔案');
        return;
      }
      setSelectedIndesignFiles(prev => ({ ...prev, [section]: file }));
      setError('');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 50 }, (_, i) => currentYear - i);
  
  const quarters = [
    { value: '', label: '── 季刊 3個月 / Quarterly ──', disabled: true },
    { value: 'QA', label: '1-3月 / Jan-Mar' },
    { value: 'QB', label: '4-6月 / Apr-Jun' },
    { value: 'QC', label: '7-9月 / Jul-Sep' },
    { value: 'QD', label: '10-12月 / Oct-Dec' },
    { value: '', label: '── 雙月刊 2個月 / Bimonthly ──', disabled: true },
    { value: 'Q1', label: '1-2月 / Jan-Feb' },
    { value: 'Q2', label: '3-4月 / Mar-Apr' },
    { value: 'Q3', label: '5-6月 / May-Jun' },
    { value: 'Q4', label: '7-8月 / Jul-Aug' },
    { value: 'Q5', label: '9-10月 / Sep-Oct' },
    { value: 'Q6', label: '11-12月 / Nov-Dec' },
  ];

  return (
    <Box sx={{ backgroundColor: colors.lightGray, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          返回首頁
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            📤 上傳新期刊
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box sx={{ mb: 3 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                處理中...
              </Typography>
            </Box>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Publication Year */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="年份 / Year"
                  {...register('publication_year', { required: true })}
                  error={!!errors.publication_year}
                  helperText={errors.publication_year && '請選擇年份'}
                  defaultValue=""
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Publication Quarter */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="季度 / Quarter"
                  {...register('publication_quarter', { required: true })}
                  error={!!errors.publication_quarter}
                  helperText={errors.publication_quarter && '請選擇季度'}
                  defaultValue=""
                >
                  {quarters.map((q, i) => (
                    <MenuItem key={q.value || `sep-${i}`} value={q.value} disabled={q.disabled}
                      sx={q.disabled ? { opacity: 0.6, fontStyle: 'italic', fontSize: '0.8rem' } : {}}>
                      {q.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* PDF File Upload */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  📄 PDF 檔案 (必填)
                </Typography>
                <Box
                  sx={{
                    border: `2px dashed ${colors.gray}`,
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: colors.royalBlue,
                      backgroundColor: 'rgba(0,0,0,0.02)',
                    },
                  }}
                  onClick={() => document.getElementById('pdf-upload').click()}
                >
                  <input
                    type="file"
                    id="pdf-upload"
                    accept=".pdf"
                    onChange={handlePdfFileChange}
                    style={{ display: 'none' }}
                  />
                  <CloudUpload sx={{ fontSize: 48, color: colors.gray, mb: 2 }} />
                  {selectedPdfFile ? (
                    <Typography variant="body1" color="primary">
                      已選擇: {selectedPdfFile.name}
                    </Typography>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      點擊選擇 PDF 檔案 (最大 100MB)
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Skip Pages */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="跳過頁碼 / Skip Pages"
                  placeholder="例如: 1,2,3 (跳過封面和廣告頁)"
                  {...register('skip_pages')}
                  helperText="輸入要跳過的 PDF 頁碼，用逗號分隔 (例如: 1,2,3 表示跳過第1、2、3頁)"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  📦 InDesign 套件 (選填 - ZIP 檔案)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  上傳 InDesign 套件可自動提取文字內容到各頁面
                </Typography>
              </Grid>

              {/* District InDesign */}
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    border: `1px dashed ${colors.gray}`,
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: colors.royalBlue },
                  }}
                  onClick={() => document.getElementById('district-indesign').click()}
                >
                  <input
                    type="file"
                    id="district-indesign"
                    accept=".zip"
                    onChange={handleIndesignFileChange('district')}
                    style={{ display: 'none' }}
                  />
                  <Typography variant="body2" fontWeight="bold">區會 / District</Typography>
                  {selectedIndesignFiles.district ? (
                    <Typography variant="caption" color="primary">
                      {selectedIndesignFiles.district.name}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      點擊上傳
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Clubs InDesign */}
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    border: `1px dashed ${colors.gray}`,
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: colors.royalBlue },
                  }}
                  onClick={() => document.getElementById('clubs-indesign').click()}
                >
                  <input
                    type="file"
                    id="clubs-indesign"
                    accept=".zip"
                    onChange={handleIndesignFileChange('clubs')}
                    style={{ display: 'none' }}
                  />
                  <Typography variant="body2" fontWeight="bold">屬會 / Clubs</Typography>
                  {selectedIndesignFiles.clubs ? (
                    <Typography variant="caption" color="primary">
                      {selectedIndesignFiles.clubs.name}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      點擊上傳
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* International InDesign */}
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    border: `1px dashed ${colors.gray}`,
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { borderColor: colors.royalBlue },
                  }}
                  onClick={() => document.getElementById('international-indesign').click()}
                >
                  <input
                    type="file"
                    id="international-indesign"
                    accept=".zip"
                    onChange={handleIndesignFileChange('international')}
                    style={{ display: 'none' }}
                  />
                  <Typography variant="body2" fontWeight="bold">國際 / International</Typography>
                  {selectedIndesignFiles.international ? (
                    <Typography variant="caption" color="primary">
                      {selectedIndesignFiles.international.name}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      點擊上傳
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={uploadMutation.isLoading || !selectedPdfFile}
                  sx={{
                    mt: 2,
                    backgroundColor: colors.royalBlue,
                    '&:hover': { backgroundColor: '#1565C0' },
                  }}
                >
                  {uploadMutation.isLoading ? '上傳中...' : '上傳期刊'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      {/* Overwrite Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelOverwrite}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"期刊已存在 (Issue Already Exists)"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {pendingFormData && (
              <>
                {pendingFormData.publication_year}年 {pendingFormData.publication_quarter} 的期刊已經存在。
                <br /><br />
                您確定要刪除舊的期刊並上傳新的嗎？此操作無法復原。
                <br />
                (An issue for {pendingFormData.publication_year} {pendingFormData.publication_quarter} already exists. Do you want to delete the old one and replace it?)
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOverwrite} color="primary" disabled={isDeleting}>
            取消 (Cancel)
          </Button>
          <Button onClick={handleConfirmOverwrite} color="error" autoFocus disabled={isDeleting}>
            {isDeleting ? '刪除中...' : '確認覆蓋 (Overwrite)'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUploadPage;
